import React, { useMemo } from 'react';
import styled from '@emotion/styled';
import { Box, Flex, OutlineButton, Text } from '@qga/roo-ui/components';
import PropTypes from 'prop-types';
import { isEmpty } from 'ramda';

import Tile from '@shared/propTypes/Tile';
import ToggleContent from '@shared/v2/components/ToggleContent';
import { BEST_RATE_ID } from '@shared/v2/constants/packages';
import { useDataLayerContext } from '@shared/v2/contexts/DataLayerContext';
import getPackageOptions from '@shared/v2/utils/getPackageOptions';

import PackageHeroSummary from '@/components/PackageHeroSummary';
import useDestination from '@/hooks/useDestination';
import useSearchQuery from '@/hooks/useSearchQuery';
import CallToActionButton from '@/shared/components/CallToActionButton';
import getLeadInPriceFromProcuredOffer from '@/utils/getLeadInPriceFromProcuredOffer';
import getPrimaryOrigin from '@/utils/getPrimaryOrigin';
import getPropertyLink from '@/utils/getPropertyLink';
import { PackagePagePriceCashAndPointsOnly } from '@/v2/components/pages/PackageView/components/PackagePagePrice';
import AllPackageOptionsModal from '@/v2/components/ui/AllPackageOptionsModal';
import { useGA4Events } from '@/v2/hooks/featureFlags/ga4Events/useGA4Events';
import { usePackagePageV2Analytics } from '@/v2/hooks/featureFlags/packagePageV2Analytics/usePackagePageV2Analytics';
import useSelectItemEvent from '@/v2/hooks/useSelectItemEvent';
import dataLayer from '@/v2/utils/dataLayer';
import { getOriginName } from '@/v2/utils/getOriginName';
import { getPackageOptionTitle } from '@/v2/utils/packageOption';
import PackageOffer from '../../../../propTypes/PackageOffer';
import SearchQuery from '../../../../propTypes/SearchQuery';
import withSkeleton from '../../../Skeleton/withSkeleton';
import {
  popupAppearedDataLayerEvent,
  propertyCTAClickedDataLayerEvent,
  viewAllOptionsClickedDataLayerEvent,
} from '../../utils/dataLayer';

const StyledOutlineButton = styled(OutlineButton)`
  padding: 10px;
  font-size: 16px;
  line-height: 24px;
  letter-spacing: 1.5px;
`;

const CallToAction = ({
  tile,
  searchQuery,
  packageOffer,
  loading,
  Skeleton,
  index,
}) => {
  const { theme, title } = useDestination() || {};
  const shouldUseNewAnalytics = usePackagePageV2Analytics();
  const { pageName } = useDataLayerContext() || {};
  const shouldUseNewEvents = useGA4Events();

  const { procuredOffer, propertyContent } = tile;

  const { displayTravellerTypes, travellers, minNumberOfNights } =
    procuredOffer.rooms[0].leadInOffers[0];

  const { id: propertyId, destination } = propertyContent;

  const { origins, name: destinationName, destinationCode } = destination;

  const { code: originCode } = getPrimaryOrigin(origins);
  const {
    searchQuery: { originCode: filteredOriginCode },
  } = useSearchQuery();

  const isPostSearch = searchQuery && !isEmpty(searchQuery);

  const leadInPrice = useMemo(() => {
    return getLeadInPriceFromProcuredOffer(procuredOffer, {
      originCode: filteredOriginCode || originCode,
      destinationCode,
    });
  }, [originCode, filteredOriginCode, destinationCode, procuredOffer]);

  const departingCity = useMemo(() => {
    return `Departing ${getOriginName(filteredOriginCode || originCode)}`;
  }, [filteredOriginCode, originCode]);

  const { packageOptions } = getPackageOptions({
    procuredOffer,
    originCode: filteredOriginCode || originCode,
    minNumberOfNights,
    commonInclusions: undefined,
    title: getPackageOptionTitle(procuredOffer),
  });

  const getOriginCode = useMemo(() => {
    return filteredOriginCode ? `?originCode=${filteredOriginCode}` : '';
  }, [filteredOriginCode]);

  const legacyHref = packageOffer
    ? getPropertyLink({
        propertyContent,
        packageOffer,
        searchQuery,
      })
    : `/${destinationName}/properties/${propertyId}/package${getOriginCode}`;

  const getHref = () => {
    const packageOptionAvailable =
      packageOptions.length >= 1 ? packageOptions[0] : undefined;

    if (packageOptionAvailable) {
      let bestPackageId;

      // pre-search: use the first package option
      if (!isPostSearch) {
        bestPackageId = packageOptionAvailable?.id;
      }
      // post-search: use 'best-rate' unless offerId matches packageId,
      // in which case, we use that offer's inclusionSetId
      else {
        bestPackageId = BEST_RATE_ID;
        procuredOffer.rooms.forEach((room) => {
          const matchingLeadIn = room.leadInOffers.find(
            (leadOffer) => leadOffer.id === packageOffer?.hotelOffer?.id,
          );
          matchingLeadIn && (bestPackageId = matchingLeadIn?.inclusionSetId);
        });
      }

      return {
        pathname: `/${tile.propertyContent.destination.name}/properties/${tile.propertyContent.id}/package`,
        query: {
          ...searchQuery,
          packageOption: bestPackageId,
        },
      };
    } else {
      return legacyHref;
    }
  };

  const priceComponent = useMemo(() => {
    if ((!packageOffer && !leadInPrice) || leadInPrice?.total?.amount <= 0) {
      return null;
    }
    return (
      <>
        {!packageOffer && (
          <PackagePagePriceCashAndPointsOnly
            displayTravellerTypes={displayTravellerTypes}
            charges={leadInPrice}
            travellers={travellers}
          />
        )}
        {packageOffer && (
          <PackagePagePriceCashAndPointsOnly
            displayTravellerTypes={displayTravellerTypes}
            charges={packageOffer.charges}
            travellers={searchQuery}
          />
        )}
      </>
    );
  }, [
    displayTravellerTypes,
    leadInPrice,
    packageOffer,
    searchQuery,
    travellers,
  ]);

  const packageOptionsAvailable = packageOptions.length > 1;
  const shouldShowViewOtherOptions = packageOptionsAvailable && !isPostSearch;

  const handleViewAllOptions = (event, show) => {
    event.preventDefault();
    if (shouldUseNewAnalytics && !!pageName) {
      viewAllOptionsClickedDataLayerEvent({ pageName });
      popupAppearedDataLayerEvent({ pageName });
    }
    if (shouldUseNewEvents) {
      dataLayer.ctaClickEvent({
        itemText: 'View all options',
      });
    }
    show();
  };

  const destinationTitle = theme ? title : `${title} holidays`;
  const name =
    pageName === 'Destination page' || pageName === 'Theme page'
      ? destinationTitle
      : pageName;

  const selectItemEventData = useSelectItemEvent({
    origins,
    originCode,
    property: tile,
    destination: destinationName,
    name,
    price: packageOffer?.charges?.total?.amount,
    index,
  });

  return (
    <Flex position="relative" flexDirection={['column', null, 'row']}>
      <Box
        pl={4}
        flex="1"
        borderLeft={!loading && '3px solid'}
        borderColor={'red'}
      >
        <Skeleton width="70%">
          <PackageHeroSummary
            searchQuery={searchQuery}
            nights={minNumberOfNights}
            showNights
          />
        </Skeleton>
        <Skeleton mt="2" width="70%">
          {priceComponent}
        </Skeleton>
        {!searchQuery && (
          <Skeleton mt="2" width="70%">
            <Text fontSize={['2xs', null, 'sm']} color="greys.steel">
              {departingCity}
            </Text>
          </Skeleton>
        )}
      </Box>
      <Skeleton mt={['4', null, '0']} width={['100%', null, '30%']}>
        <Flex mt={['4', null, '0']} flexDirection={'column'}>
          <CallToActionButton
            data-testid="CTA_BUTTON"
            variant="primary"
            width={['100%', null, '230px']}
            href={getHref()}
            onClick={() => {
              if (shouldUseNewAnalytics && !!pageName) {
                propertyCTAClickedDataLayerEvent({
                  pageName,
                  isPresearch: !searchQuery,
                });
              }
              if (shouldUseNewEvents) {
                dataLayer.selectItemEvent(selectItemEventData);
                dataLayer.ctaClickEvent({
                  itemText: searchQuery
                    ? 'SELECT PACKAGE'
                    : 'CHECK AVAILABILITY',
                  url: getHref()?.pathname,
                });
              }
            }}
            mb={shouldShowViewOtherOptions ? 4 : null}
          >
            {searchQuery ? 'SELECT PACKAGE' : 'CHECK AVAILABILITY'}
          </CallToActionButton>

          {shouldShowViewOtherOptions && (
            <ToggleContent
              toggle={(show) => (
                <StyledOutlineButton
                  type="button"
                  variant="primary"
                  data-testid="VIEW_ALL_OPTIONS_CTA"
                  width={['100%', null, '230px']}
                  onClick={(event) => handleViewAllOptions(event, show)}
                >
                  VIEW ALL OPTIONS
                </StyledOutlineButton>
              )}
              content={(close) => (
                <AllPackageOptionsModal
                  onClose={close}
                  tile={tile}
                  packageOptions={packageOptions}
                />
              )}
            />
          )}
        </Flex>
      </Skeleton>
    </Flex>
  );
};

CallToAction.propTypes = {
  tile: Tile.isRequired,
  Skeleton: PropTypes.func.isRequired,
  packageOffer: PropTypes.shape(PackageOffer),
  searchQuery: PropTypes.shape(SearchQuery),
  index: PropTypes.number.isRequired,
};

const CallToActionWithSkeleton = withSkeleton(({ loading }) => loading)(
  CallToAction,
);

export default CallToActionWithSkeleton;
